import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components/macro';
import { Icon } from '../../icon/Icon';
import { device } from '../../../Theme';
import { rgba } from 'polished';

export const SearchInput = forwardRef(
  (
    {
      id,
      label,
      strings,
      handleSubmit,
      handleChange,
      handleFocus,
      handleClear,
      isHeader,
      searchKeyword,
      hasSearchKeyword,
      autoFocus,
      ...props
    },
    ref
  ) => {
    const inputRef = useRef(null);

    const submitHandler = (event) => {
      event.preventDefault();
    };

    const handleSearch = (event) => {
      const { value } = event?.target;
      handleSubmit(value);
      inputRef.current.value = '';
      window.scrollTo(0, 0);
    };

    const clearSearch = () => {
      handleClear();
      inputRef.current.value = '';
    };

    const handleSearchSubmit = (event) => {
      if (isHeader) {
        hasSearchKeyword
          ? clearSearch()
          : hasSearchKeyword && handleSearch(event);
      } else {
        inputRef.current.value && handleSearch(event);
      }
    };

    const handleEnterPress = (event) => {
      if (event.key === 'Enter') {
        inputRef.current.value && handleSearch(event);
      }
    };

    useImperativeHandle(ref, () => ({
      clearSearchInput() {
        inputRef.current.value = '';
      },
    }));

    label = label ? label : strings.searchClic;

    return (
      <ClicSearchForm ref={ref} role="search" onSubmit={submitHandler}>
        <Label htmlFor={id}>{label}</Label>
        <ClicSearch key={searchKeyword}>
          <Input
            ref={inputRef}
            id={id}
            type="text"
            name="search"
            placeholder={label}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            defaultValue={searchKeyword}
            onChange={handleChange}
            onFocus={handleFocus}
            autoFocus={autoFocus}
            onKeyDown={handleEnterPress}
          />
          <Button
            aria-label="Search"
            type="button"
            onClick={handleSearchSubmit}
          >
            <Icon
              materialLabel={isHeader && hasSearchKeyword ? 'close' : 'search'}
              size="large"
              type="material"
            />
          </Button>
        </ClicSearch>
      </ClicSearchForm>
    );
  }
);

const ClicSearchForm = styled.form`
  width: 100%;
  @media ${device.tablet} {
    max-width: 60%;
  }
`;

const ClicSearch = styled.div`
  display: flex;
  position: relative;
  flex-direction: row;
  background: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.black5
      : props.theme.color.white5};
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? `0px 0px 0px 1px ${props.theme.color.black10}`
      : `0px 0px 0px 1px ${rgba(props.theme.color.whiteFull, 0.2)}`};
  border-radius: 8px;
  backdrop-filter: blur(60px);
  &:hover {
    box-shadow: ${(props) =>
      props.theme.name === 'light'
        ? `0px 0px 0px 1px ${props.theme.color.black10}`
        : `0px 0px 0px 1px ${rgba(props.theme.color.whiteFull, 0.2)}`};
    span {
      background-color: ${(props) =>
        props.theme.name === 'Light Theme'
          ? props.theme.color.primaryAA
          : props.theme.color.primaryFull};
    }
  }
  &:active {
    box-shadow: ${(props) =>
      props.theme.name === 'light'
        ? `0px 0px 0px 1px ${props.theme.color.black10}`
        : `0px 0px 0px 1px ${props.theme.color.white10}`};
    span {
      background-color: ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.primaryAA
          : props.theme.color.whiteFull};
    }
  }
  &:focus-visible {
    span {
      background-color: ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.blackFull
          : props.theme.color.whiteFull};
    }
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

const Label = styled.label`
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 52px 14px 12px;
  background: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.black5
      : props.theme.color.white5};
  border: none;
  border-radius: 8px;
  color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.blackFull
      : props.theme.color.whiteFull};
  font-family: 'Gotham Narrow SSm 4r', 'Gotham Narrow SSm A',
    'Gotham Narrow SSm B', 'HelveticaNeue-Regular', 'Helvetica Neue Regular',
    'Helvetica Neue', Helvetica, Verdana, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
  &::placeholder {
    opacity: 0.75;
    font-weight: 400;
  }
  &:hover,
  &:active,
  &:focus-visible {
    color: ${(props) =>
      props.theme.name === 'dark' && props.theme.color.whiteFull};
  }
  &:focus-visible {
    box-shadow: ${(props) => props.theme.focusRing};
    outline: none;
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

const Button = styled.button`
  position: absolute;
  right: 0;
  padding: 13px 12px;
  height: 50px;
  background: ${(props) =>
    props.theme.name === 'light'
      ? `${rgba(props.theme.color.blackFull, 0)}`
      : `${rgba(props.theme.color.whiteFull, 0)}`};
  border: 0;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  cursor: pointer;
  span {
    color: ${(props) =>
      props.theme.name === 'light'
        ? props.theme.color.blackFull
        : props.theme.color.whiteFull};
    font-size: 24px;
  }
  &:focus-visible {
    box-shadow: ${(props) => props.theme.focusRing};
    outline: none;
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
`;
